import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { tomApi, organizationApi } from '../services/api';
import {
  TomMeasureCreate,
  TomMeasureUpdate,
  TomTemplate,
  TomCategory,
  TomStatus,
  TomScope,
  TomEffectiveness
} from '@dsgvo-buddy/types';
import {
  Title,
  Text,
  Button,
  Input,
  TextArea,
  Select,
  Option,
  ProgressIndicator,
  FlexBox,
  Card,
  Label,
  Icon,
  MessageStrip,
  BusyIndicator,
  RadioButton,
  DatePicker
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';
import HelpTooltip from '../components/HelpTooltip';
import { fieldHelp } from '../helpers/helpTexts';
import { isoToGerman, germanToIso } from '../helpers/dateFormat';

interface TomWizardPageProps {
  editMode?: boolean;
}

const categoryConfig: Record<TomCategory, { label: string; description: string }> = {
  [TomCategory.ACCESS_CONTROL]: { label: 'Zugriffskontrolle', description: 'Maßnahmen zur Kontrolle des Zugangs zu Systemen und Daten' },
  [TomCategory.TRANSFER_CONTROL]: { label: 'Weitergabekontrolle', description: 'Schutz bei der Übertragung personenbezogener Daten' },
  [TomCategory.INPUT_CONTROL]: { label: 'Eingabekontrolle', description: 'Nachvollziehbarkeit von Dateneingaben und -änderungen' },
  [TomCategory.AVAILABILITY]: { label: 'Verfügbarkeit', description: 'Sicherstellung der Erreichbarkeit von Systemen und Daten' },
  [TomCategory.RESILIENCE]: { label: 'Belastbarkeit', description: 'Widerstandsfähigkeit gegen Störungen und Angriffe' },
  [TomCategory.RECOVERY]: { label: 'Wiederherstellung', description: 'Fähigkeit zur Wiederherstellung nach Ausfällen' },
  [TomCategory.SEPARATION]: { label: 'Trennbarkeit', description: 'Getrennte Verarbeitung von Daten verschiedener Zwecke' },
  [TomCategory.DELETION]: { label: 'Löschung', description: 'Sichere und fristgerechte Löschung personenbezogener Daten' },
  [TomCategory.PRIVACY_BY_DESIGN]: { label: 'Privacy by Design/Default', description: 'Datenschutz durch Technikgestaltung und Voreinstellungen' },
  [TomCategory.ENCRYPTION]: { label: 'Verschlüsselung', description: 'Verschlüsselung von Daten bei Speicherung und Übertragung' },
  [TomCategory.PSEUDONYMIZATION]: { label: 'Pseudonymisierung', description: 'Ersetzung identifizierender Merkmale durch Pseudonyme' }
};

const statusConfig: Record<TomStatus, { label: string; description: string }> = {
  [TomStatus.PLANNED]: { label: 'Geplant', description: 'Maßnahme ist definiert, aber noch nicht umgesetzt' },
  [TomStatus.IMPLEMENTED]: { label: 'Umgesetzt', description: 'Maßnahme ist vollständig implementiert und wirksam' },
  [TomStatus.OUTDATED]: { label: 'Veraltet', description: 'Maßnahme ist nicht mehr aktuell und muss überarbeitet werden' },
  [TomStatus.COMPENSATING]: { label: 'Kompensierend', description: 'Ersatzmaßnahme für eine nicht umsetzbare Primärmaßnahme' }
};

const effectivenessConfig: Record<TomEffectiveness, { label: string; description: string }> = {
  [TomEffectiveness.HIGH]: { label: 'Hoch', description: 'Sehr wirksam, reduziert Risiko erheblich' },
  [TomEffectiveness.MEDIUM]: { label: 'Mittel', description: 'Wirksam, reduziert Risiko teilweise' },
  [TomEffectiveness.LOW]: { label: 'Niedrig', description: 'Begrenzte Wirksamkeit, zusätzliche Maßnahmen empfohlen' },
  [TomEffectiveness.NOT_ASSESSED]: { label: 'Nicht bewertet', description: 'Wirksamkeit wurde noch nicht bewertet' }
};

const steps = [
  { title: 'Grunddaten', icon: 'shield' },
  { title: 'Kategorie & Status', icon: 'quality-issue' },
  { title: 'Bewertung & Prüfung', icon: 'inspection' }
];

interface FormData {
  organization_id: number | null;
  name: string;
  description: string;
  category: TomCategory;
  status: TomStatus;
  scope: TomScope;
  effectiveness: TomEffectiveness;
  responsible_person: string;
  review_date: string;
  evidence_reference: string;
  notes: string;
}

const defaultFormData: FormData = {
  organization_id: null,
  name: '',
  description: '',
  category: TomCategory.ACCESS_CONTROL,
  status: TomStatus.PLANNED,
  scope: TomScope.GLOBAL,
  effectiveness: TomEffectiveness.NOT_ASSESSED,
  responsible_person: '',
  review_date: '',
  evidence_reference: '',
  notes: ''
};

export default function TomWizardPage({ editMode = false }: TomWizardPageProps) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [currentStep, setCurrentStep] = useState(0);
  const [templates, setTemplates] = useState<TomTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(editMode);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(defaultFormData);

  useEffect(() => { loadInitialData(); }, []);

  const loadInitialData = async () => {
    const [orgsRes, templatesRes] = await Promise.all([
      organizationApi.getAll(),
      tomApi.getTemplates()
    ]);

    if (orgsRes.success && orgsRes.data) {
      if (orgsRes.data.length > 0 && !editMode) {
        setFormData(prev => ({ ...prev, organization_id: orgsRes.data![0].id }));
      }
    }

    if (templatesRes.success && templatesRes.data) {
      setTemplates(templatesRes.data);
    }

    if (editMode && id) {
      const org = orgsRes.data?.[0];
      if (org) {
        const res = await tomApi.getById(parseInt(id), org.id);
        if (res.success && res.data) {
          const m = res.data;
          setFormData({
            organization_id: m.organization_id,
            name: m.name,
            description: m.description || '',
            category: m.category,
            status: m.status,
            scope: m.scope,
            effectiveness: m.effectiveness,
            responsible_person: m.responsible_person || '',
            review_date: m.review_date || '',
            evidence_reference: m.evidence_reference || '',
            notes: m.notes || ''
          });
        } else {
          navigate('/tom');
        }
      }
    }

    setInitialLoading(false);
  };

  const applyTemplate = (template: TomTemplate) => {
    setFormData(prev => ({
      ...prev,
      name: template.name,
      description: template.description,
      category: template.category,
      scope: template.scope,
      effectiveness: template.effectiveness
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    if (editMode && id) {
      const updates: TomMeasureUpdate = {
        name: formData.name,
        description: formData.description || undefined,
        category: formData.category,
        status: formData.status,
        scope: formData.scope,
        effectiveness: formData.effectiveness,
        responsible_person: formData.responsible_person || undefined,
        review_date: formData.review_date || undefined,
        evidence_reference: formData.evidence_reference || undefined,
        notes: formData.notes || undefined
      };
      const response = await tomApi.update(parseInt(id), formData.organization_id!, updates);
      if (response.success) {
        navigate(`/tom/${id}`);
      } else {
        setError(response.error || 'Fehler beim Aktualisieren');
      }
    } else {
      const data: TomMeasureCreate = {
        organization_id: formData.organization_id!,
        name: formData.name,
        description: formData.description || undefined,
        category: formData.category,
        status: formData.status,
        scope: formData.scope,
        effectiveness: formData.effectiveness,
        responsible_person: formData.responsible_person || undefined,
        review_date: formData.review_date || undefined,
        evidence_reference: formData.evidence_reference || undefined,
        notes: formData.notes || undefined
      };
      const response = await tomApi.create(data);
      if (response.success && response.data) {
        navigate(`/tom/${response.data.id}`);
      } else {
        setError(response.error || 'Fehler beim Erstellen');
      }
    }

    setLoading(false);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return formData.name.trim() !== '';
      case 1: return true;
      case 2: return true;
      default: return false;
    }
  };

  if (initialLoading) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  return (
    <div className="page-layout--narrow">
      {/* Header */}
      <FlexBox justifyContent="SpaceBetween" alignItems="Center" style={{ marginBottom: '1.5rem' }}>
        <div>
          <Title level="H3" style={{ marginBottom: '0.25rem' }}>
            {editMode ? 'TOM-Maßnahme bearbeiten' : 'Neue TOM-Maßnahme'}
          </Title>
          <Text style={{ color: 'var(--sapNeutralTextColor)' }}>
            Technische und organisatorische Maßnahme gemäß Art. 32 DSGVO
          </Text>
        </div>
        <Button icon="decline" design="Transparent" onClick={() => navigate('/tom')}>
          Abbrechen
        </Button>
      </FlexBox>

      {/* Progress */}
      <FlexBox style={{ gap: '0.5rem', marginBottom: '1.5rem' }}>
        {steps.map((step, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              padding: '0.75rem',
              borderRadius: '0.5rem',
              background: i === currentStep
                ? 'var(--sapSelectedColor, #0070f2)'
                : i < currentStep
                  ? 'var(--sapPositiveColor, #107e3e)'
                  : 'var(--sapNeutralBackground, #f5f6f7)',
              color: i <= currentStep ? 'white' : 'var(--sapNeutralTextColor)',
              textAlign: 'center',
              cursor: i < currentStep ? 'pointer' : 'default',
              transition: 'background 0.2s'
            }}
            onClick={() => { if (i < currentStep) setCurrentStep(i); }}
          >
            <Icon name={step.icon} style={{ marginRight: '0.5rem' }} />
            <Text style={{ fontWeight: '600', color: 'inherit', fontSize: '0.8125rem' }}>
              {i + 1}. {step.title}
            </Text>
          </div>
        ))}
      </FlexBox>

      <ProgressIndicator
        value={((currentStep + 1) / steps.length) * 100}
        style={{ marginBottom: '1.5rem' }}
      />

      {error && (
        <MessageStrip design="Negative" style={{ marginBottom: '1rem' }}>
          {error}
        </MessageStrip>
      )}

      {/* Step Content */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <div style={{ padding: '1.5rem' }}>
          {currentStep === 0 && (
            <div>
              <Title level="H5" style={{ marginBottom: '1rem' }}>Grunddaten der Maßnahme</Title>

              {/* Templates */}
              {!editMode && templates.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <Label style={{ fontWeight: '600', display: 'block', marginBottom: '0.5rem' }}>
                    Vorlage verwenden
                  </Label>
                  <FlexBox wrap="Wrap" style={{ gap: '0.5rem' }}>
                    {templates.slice(0, 8).map((t, i) => (
                      <Button
                        key={i}
                        design="Default"
                        onClick={() => applyTemplate(t)}
                        style={{ fontSize: '0.8125rem' }}
                      >
                        {t.name}
                      </Button>
                    ))}
                    {templates.length > 8 && (
                      <Select
                        onChange={(e: any) => {
                          const idx = parseInt(e.detail.selectedOption.value);
                          if (!isNaN(idx)) applyTemplate(templates[idx]);
                        }}
                        style={{ minWidth: '200px' }}
                      >
                        <Option value="">Weitere Vorlagen...</Option>
                        {templates.slice(8).map((t, i) => (
                          <Option key={i + 8} value={(i + 8).toString()}>{t.name}</Option>
                        ))}
                      </Select>
                    )}
                  </FlexBox>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <Label required>Name der Maßnahme</Label>
                    <HelpTooltip text={fieldHelp.tom.name} inline />
                  </div>
                  <Input
                    value={formData.name}
                    onInput={(e: any) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="z.B. Zwei-Faktor-Authentifizierung"
                    style={{ width: '100%' }}
                  />
                </div>

                <div>
                  <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Beschreibung</Label>
                  <TextArea
                    value={formData.description}
                    onInput={(e: any) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Detaillierte Beschreibung der Maßnahme..."
                    rows={4}
                    style={{ width: '100%' }}
                  />
                </div>

                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <Label>Verantwortliche Person</Label>
                    <HelpTooltip text={fieldHelp.tom.responsible_person} inline />
                  </div>
                  <Input
                    value={formData.responsible_person}
                    onInput={(e: any) => setFormData(prev => ({ ...prev, responsible_person: e.target.value }))}
                    placeholder="Name der verantwortlichen Person"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div>
              <Title level="H5" style={{ marginBottom: '1rem' }}>Kategorie und Status</Title>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                {/* Category */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <Label required style={{ fontWeight: '600' }}>TOM-Kategorie</Label>
                    <HelpTooltip text={fieldHelp.tom.category} inline />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.5rem' }}>
                    {Object.entries(categoryConfig).map(([key, cfg]) => (
                      <div
                        key={key}
                        style={{
                          padding: '0.75rem',
                          borderRadius: '0.375rem',
                          border: formData.category === key
                            ? '2px solid var(--sapSelectedColor, #0070f2)'
                            : '1px solid var(--sapGroup_ContentBorderColor, #e5e5e5)',
                          background: formData.category === key ? 'var(--sapSelectedBackground, #e8f0fe)' : 'transparent',
                          cursor: 'pointer'
                        }}
                        onClick={() => setFormData(prev => ({ ...prev, category: key as TomCategory }))}
                      >
                        <Text style={{ fontWeight: '600', fontSize: '0.875rem' }}>{cfg.label}</Text>
                        <Text style={{ display: 'block', fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', marginTop: '0.125rem' }}>
                          {cfg.description}
                        </Text>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Status */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <Label style={{ fontWeight: '600' }}>Status</Label>
                    <HelpTooltip text={fieldHelp.tom.status} inline />
                  </div>
                  <FlexBox wrap="Wrap" style={{ gap: '0.5rem' }}>
                    {Object.entries(statusConfig).map(([key, cfg]) => (
                      <div
                        key={key}
                        style={{
                          padding: '0.75rem 1rem',
                          borderRadius: '0.375rem',
                          border: formData.status === key
                            ? '2px solid var(--sapSelectedColor, #0070f2)'
                            : '1px solid var(--sapGroup_ContentBorderColor, #e5e5e5)',
                          background: formData.status === key ? 'var(--sapSelectedBackground, #e8f0fe)' : 'transparent',
                          cursor: 'pointer',
                          flex: '1 1 200px'
                        }}
                        onClick={() => setFormData(prev => ({ ...prev, status: key as TomStatus }))}
                      >
                        <Text style={{ fontWeight: '600', fontSize: '0.875rem' }}>{cfg.label}</Text>
                        <Text style={{ display: 'block', fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', marginTop: '0.125rem' }}>
                          {cfg.description}
                        </Text>
                      </div>
                    ))}
                  </FlexBox>
                </div>

                {/* Scope */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <Label style={{ fontWeight: '600' }}>Geltungsbereich</Label>
                    <HelpTooltip text={fieldHelp.tom.scope} inline />
                  </div>
                  <FlexBox style={{ gap: '1rem' }}>
                    <RadioButton
                      name="scope"
                      text="Global (organisationsweit)"
                      checked={formData.scope === TomScope.GLOBAL}
                      onChange={() => setFormData(prev => ({ ...prev, scope: TomScope.GLOBAL }))}
                    />
                    <RadioButton
                      name="scope"
                      text="Pro Verarbeitung (individuell)"
                      checked={formData.scope === TomScope.PER_PROCESSING}
                      onChange={() => setFormData(prev => ({ ...prev, scope: TomScope.PER_PROCESSING }))}
                    />
                  </FlexBox>
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <Title level="H5" style={{ marginBottom: '1rem' }}>Bewertung und Prüfung</Title>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                {/* Effectiveness */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <Label style={{ fontWeight: '600' }}>Wirksamkeit</Label>
                    <HelpTooltip text={fieldHelp.tom.effectiveness} inline />
                  </div>
                  <FlexBox wrap="Wrap" style={{ gap: '0.5rem' }}>
                    {Object.entries(effectivenessConfig).map(([key, cfg]) => (
                      <div
                        key={key}
                        style={{
                          padding: '0.75rem 1rem',
                          borderRadius: '0.375rem',
                          border: formData.effectiveness === key
                            ? '2px solid var(--sapSelectedColor, #0070f2)'
                            : '1px solid var(--sapGroup_ContentBorderColor, #e5e5e5)',
                          background: formData.effectiveness === key ? 'var(--sapSelectedBackground, #e8f0fe)' : 'transparent',
                          cursor: 'pointer',
                          flex: '1 1 180px'
                        }}
                        onClick={() => setFormData(prev => ({ ...prev, effectiveness: key as TomEffectiveness }))}
                      >
                        <Text style={{ fontWeight: '600', fontSize: '0.875rem' }}>{cfg.label}</Text>
                        <Text style={{ display: 'block', fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', marginTop: '0.125rem' }}>
                          {cfg.description}
                        </Text>
                      </div>
                    ))}
                  </FlexBox>
                </div>

                {/* Review Date */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <Label>Nächster Prüftermin</Label>
                    <HelpTooltip text={fieldHelp.tom.review_date} inline />
                  </div>
                  <DatePicker
                    formatPattern="dd.MM.yyyy"
                    value={isoToGerman(formData.review_date)}
                    onChange={(e: any) => setFormData(prev => ({ ...prev, review_date: germanToIso(e.detail.value) }))}
                    style={{ width: '100%', maxWidth: '300px' }}
                  />
                </div>

                {/* Evidence Reference */}
                <div>
                  <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Nachweis / Referenz</Label>
                  <Input
                    value={formData.evidence_reference}
                    onInput={(e: any) => setFormData(prev => ({ ...prev, evidence_reference: e.target.value }))}
                    placeholder="z.B. Dokument-Referenz, Ticket-ID, Link..."
                    style={{ width: '100%' }}
                  />
                </div>

                {/* Notes */}
                <div>
                  <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Notizen</Label>
                  <TextArea
                    value={formData.notes}
                    onInput={(e: any) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Zusätzliche Hinweise oder Anmerkungen..."
                    rows={3}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Navigation Buttons */}
      <FlexBox justifyContent="SpaceBetween">
        <Button
          icon="nav-back"
          design="Transparent"
          onClick={() => {
            if (currentStep > 0) setCurrentStep(currentStep - 1);
            else navigate('/tom');
          }}
        >
          {currentStep > 0 ? 'Zurück' : 'Abbrechen'}
        </Button>

        {currentStep < steps.length - 1 ? (
          <Button
            icon="navigation-right-arrow"
            iconEnd
            design="Emphasized"
            onClick={() => setCurrentStep(currentStep + 1)}
            disabled={!canProceed()}
          >
            Weiter
          </Button>
        ) : (
          <Button
            icon="save"
            design="Emphasized"
            onClick={handleSubmit}
            disabled={loading || !formData.name.trim()}
          >
            {loading ? 'Wird gespeichert...' : editMode ? 'Aktualisieren' : 'Maßnahme erstellen'}
          </Button>
        )}
      </FlexBox>
    </div>
  );
}
